import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { IconEye } from "@tabler/icons-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convex-api";

type RunnerStatus = "pending" | "approved" | "rejected" | "all";

export const Route = createFileRoute("/_authenticated/runners/")({
	component: RunnerPage,
});

function RunnerPage() {
	const [statusFilter, setStatusFilter] = useState<RunnerStatus>("pending");

	const data = useQuery(api.admin.listRunners, {
		status: statusFilter,
		limit: 50,
		offset: 0,
	});
	const isLoading = data === undefined;

	const getStatusBadge = (status: string) => {
		switch (status) {
			case "verified":
				return <Badge className="bg-green-500">Approved</Badge>;
			case "rejected":
				return <Badge variant="destructive">Rejected</Badge>;
			case "pending":
				return <Badge variant="secondary">Pending</Badge>;
			default:
				return <Badge>{status}</Badge>;
		}
	};

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-8 flex items-center justify-between">
				<div>
					<h1 className="font-bold text-3xl">Runner Management</h1>
					<p className="mt-1 text-muted-foreground text-sm">
						Review KYC applications and manage runner accounts.
					</p>
				</div>
				<Select
					onValueChange={(value) => setStatusFilter(value as RunnerStatus)}
					value={statusFilter}
				>
					<SelectTrigger className="w-45">
						<SelectValue placeholder="Filter by status" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All</SelectItem>
						<SelectItem value="pending">Pending</SelectItem>
						<SelectItem value="approved">Approved</SelectItem>
						<SelectItem value="rejected">Rejected</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{isLoading ? (
				<div className="space-y-3">
					{Array.from({ length: 5 }).map((_, i) => (
						<Skeleton className="h-12 w-full" key={`skeleton-${String(i)}`} />
					))}
				</div>
			) : !data || data.runners.length === 0 ? (
				<div className="flex items-center justify-center py-10">
					<p className="text-muted-foreground">No runners found</p>
				</div>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Email</TableHead>
								<TableHead>Phone</TableHead>
								<TableHead>Work Location</TableHead>
								<TableHead>Submitted At</TableHead>
								<TableHead>Email Verified</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{data.runners.map((runner: (typeof data.runners)[number]) => (
								<TableRow key={runner.id}>
									<TableCell className="font-medium">
										{runner.userName}
									</TableCell>
									<TableCell>{runner.userEmail}</TableCell>
									<TableCell>{runner.userPhone ?? "N/A"}</TableCell>
									<TableCell>{runner.workLocation ?? "N/A"}</TableCell>
									<TableCell>
										{runner.kycSubmittedAt
											? new Date(runner.kycSubmittedAt).toLocaleDateString()
											: "N/A"}
									</TableCell>
									<TableCell>
										{runner.userEmailVerified ? (
											<Badge className="bg-green-500">Verified</Badge>
										) : (
											<Badge variant="destructive">Unverified</Badge>
										)}
									</TableCell>
									<TableCell>
										{getStatusBadge(runner.kycStatus ?? "pending")}
									</TableCell>
									<TableCell>
										<Button asChild size="sm" variant="outline">
											<Link
												to="/runners/$userId"
												params={{ userId: runner.id }}
											>
												<IconEye className="mr-2 h-4 w-4" />
												Review
											</Link>
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
